import { NavLink } from "react-router-dom";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, ChevronRight, HomeIcon } from "lucide-react";
import { NsKey } from "enums/NsKey";
import { useAbp } from "core/hooks/useAbp";
import { SimpleDashboardContent } from "features/dashboard/DashboardContent";
import { GenericViewHeader } from "back-office/shared/components/GenericViewHeader";
import { BoMenuGroup, boMenuGroups } from "./dashboard/boMenuItems";

/** Sous-titres des accès directs (groupes sans sous-menu). */
const DIRECT_DESCRIPTIONS: Record<string, string> = {
  statistics: "Tableaux de bord et indicateurs — en cours de développement",
  documents: "Tous les documents et fichiers du back-office"
};

/**
 * Accueil du back-office : accès rapide aligné sur les groupes du menu latéral
 * (mêmes sections, mêmes permissions) — une carte par groupe, ses entrées à l'intérieur.
 */
const BackofficeHome = () => {
  const { t } = useTranslation(NsKey.Common);
  const { hasPermission } = useAbp();

  // Même filtrage que le menu : entrées permissionnées, groupes vides masqués, sans Accueil
  const groups = useMemo(
    () =>
      boMenuGroups
        .filter((group) => group.id !== "home")
        .filter((group) => !group.permission || hasPermission(group.permission))
        .map((group) => ({
          ...group,
          items: group.items?.filter(
            (item) => !item.permission || hasPermission(item.permission)
          )
        }))
        .filter((group) => group.to || (group.items && group.items.length > 0)),
    [hasPermission]
  );

  // En-tête commun des cartes : bandeau primary avec l'icône du groupe
  const cardHeader = (group: BoMenuGroup) => {
    const Icon = group.icon;
    return (
      <div className="flex items-center gap-3 bg-primary px-5 py-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 text-white">
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="text-base font-semibold text-white">{t(group.label)}</h3>
      </div>
    );
  };

  const renderGroupCard = (group: BoMenuGroup) => {
    // Accès direct (Statistiques, Documents…) : toute la carte est un lien
    if (group.to) {
      return (
        <NavLink
          key={group.id}
          to={group.to}
          className="group flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg"
        >
          {cardHeader(group)}
          <div className="flex flex-1 items-center justify-between gap-3 p-5">
            <p className="text-sm text-gray-500">
              {DIRECT_DESCRIPTIONS[group.id] ?? ""}
            </p>
            <ArrowRight className="h-5 w-5 shrink-0 text-gray-300 transition-all group-hover:translate-x-1 group-hover:text-primary" />
          </div>
        </NavLink>
      );
    }

    // Groupe à sous-menu : carte de section avec ses entrées cliquables
    return (
      <div
        key={group.id}
        className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-lg"
      >
        {cardHeader(group)}
        <div className="flex-1 p-2.5">
          {group.items!.map((item) => {
            const ItemIcon = item.icon;
            return (
              <NavLink
                key={item.id}
                to={item.to!}
                className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-gray-700 transition-colors hover:bg-primary/5 hover:text-primary"
              >
                <ItemIcon className="h-4 w-4 text-gray-400 transition-colors group-hover:text-primary" />
                <span className="flex-1">{t(item.label)}</span>
                <ChevronRight className="h-4 w-4 text-gray-300 transition-all group-hover:translate-x-0.5 group-hover:text-primary" />
              </NavLink>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <SimpleDashboardContent>
      <GenericViewHeader
        title="Accueil"
        icon={HomeIcon}
        showBreadcrumb={false}
      />
      <p className="-mt-3 mb-8 text-sm text-gray-500">
        Bienvenue sur votre espace de gestion.
      </p>

      <h2 className="mb-4 text-sm font-bold uppercase tracking-[0.14em] text-primary/60">
        Accès rapide
      </h2>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
        {groups.map(renderGroupCard)}
      </div>
    </SimpleDashboardContent>
  );
};

export default BackofficeHome;
